import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Scissors, Sparkles, User, Heart, Paintbrush, Droplet, Check, Bath, UserRound, Wind } from 'lucide-react';
import { siteInfo, services, serviceDetails } from '../data/siteData';

const Navbar = ({ onBookingClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [hoveredService, setHoveredService] = useState('hairCare');
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if current page is homepage
  const isHomePage = location.pathname === '/';
  
  // Check if current page is Thank You page
  const isThankYouPage = location.pathname === '/thank-you' || location.pathname === '/thank-you-demo';

  // Scroll detection effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  // Icon mapping for services
  const serviceIcons = {
    hairCare: Scissors,
    faceCare: Bath,        // Bath/Spa icon represents facial treatments and skincare
    beardCare: UserRound,  // Round user icon better represents grooming/personal care
    relaxation: Wind       // Wind icon represents calm, relaxation, and therapeutic atmosphere
  };

  // Icon mapping for key service details
  const getServiceDetailIcon = (serviceKey, index) => {
    const iconMaps = {
      hairCare: [Scissors, Paintbrush, Droplet],
      faceCare: [Sparkles, Droplet, Check],
      beardCare: [Scissors, User, Droplet],
      relaxation: [Heart, Droplet, Sparkles]
    };
    return iconMaps[serviceKey]?.[index] || Check;
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' }
  ];

  const navLinksAfterServices = [
    { path: '/contact', label: 'Contact Us' }
  ];

  const handleServiceClick = (serviceKey) => {
    const slug = serviceDetails[serviceKey]?.slug;
    if (slug) {
      navigate(`/services/${slug}`);
    } else {
      navigate('/services', { state: { activeService: serviceKey } });
    }
    setIsServicesDropdownOpen(false);
    setIsMenuOpen(false);
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300`}
    style={{
      backgroundColor: !isHomePage ? '#000000' : (isScrolled ? '#000000' : 'transparent'),
      boxShadow: (!isHomePage || isScrolled) ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none'
    }}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'transparent' }}>
        <div className="flex justify-between items-center h-20" style={{ backgroundColor: 'transparent' }}>
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img 
              src="/logo-white.png" 
              alt="LookWell Gents Salon Logo" 
              className="h-12 sm:h-14 md:h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            {/* Hide navigation links on Thank You page */}
            {!isThankYouPage && (
              <>
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`font-medium transition-colors ${
                      isActive(link.path)
                        ? 'text-amber-500'
                        : 'text-white hover:text-amber-500'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                
                {/* Services Mega Dropdown */}
                <div 
                  className="relative group"
                >
              <button
                onMouseEnter={() => setIsServicesDropdownOpen(true)}
                className={`font-medium transition-colors flex items-center gap-1 ${
                  isActive('/services')
                    ? 'text-amber-500'
                    : 'text-white hover:text-amber-500'
                }`}
              >
                Services
                <ChevronDown size={16} className={`transition-transform ${isServicesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Mega Dropdown Menu - Full Width */}
              {isServicesDropdownOpen && (
                <div 
                  className="fixed top-20 left-0 right-0 w-full bg-white shadow-2xl overflow-hidden z-50"
                  onMouseEnter={() => setIsServicesDropdownOpen(true)}
                  onMouseLeave={() => setIsServicesDropdownOpen(false)}
                >
                  <div className="grid grid-cols-12 gap-0 h-[500px]">
                    
                    {/* LEFT - Service Names List */}
                    <div className="col-span-4 bg-white p-6 overflow-y-auto">
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Our Services</h3>
                      <div className="space-y-2">
                        {Object.entries(services).map(([key, service]) => {
                          const IconComponent = serviceIcons[key];
                          return (
                            <button
                              key={key}
                              onMouseEnter={() => setHoveredService(key)}
                              onClick={() => handleServiceClick(key)}
                              className={`w-full text-left px-4 py-3.5 rounded-lg transition-all duration-300 flex items-center gap-3 group ${
                                hoveredService === key
                                  ? 'bg-amber-500 text-black shadow-lg scale-105'
                                  : 'hover:bg-gray-50 text-gray-700'
                              }`}
                            >
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                hoveredService === key ? 'bg-white' : 'bg-amber-50 group-hover:bg-amber-100'
                              }`}>
                                <IconComponent 
                                  size={20} 
                                  className={hoveredService === key ? 'text-amber-500' : 'text-amber-500'} 
                                />
                              </div>
                              <div>
                                <span className="font-bold text-base block">{service.title}</span>
                                <span className={`text-xs ${hoveredService === key ? 'text-black/70' : 'text-gray-500'}`}>
                                  {service.services?.length || 0} Services
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* RIGHT - Large Image with Content Overlay */}
                    <div className="col-span-8 relative overflow-hidden bg-black">
                      {/* Background Image - Zoomed Out */}
                      <img 
                        src={services[hoveredService].image}
                        alt={services[hoveredService].title}
                        className="w-full h-full object-cover"
                        style={{ 
                          objectPosition: 'center center',
                          transform: 'scale(0.85)'
                        }}
                      />
                      
                      {/* Dark Overlay for Content Readability */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30"></div>
                      
                      {/* Content on Top of Image */}
                      <div className="absolute inset-0 flex flex-col justify-end p-6">
                        {/* Service Icon & Title */}
                        <div className="mb-3">
                          <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-3">
                            {React.createElement(serviceIcons[hoveredService], { 
                              size: 24, 
                              className: 'text-white' 
                            })}
                          </div>
                          <h3 className="text-3xl font-bold text-white mb-1">
                            {services[hoveredService].title}
                          </h3>
                          <p className="text-amber-400 text-sm font-medium mb-3">
                            {services[hoveredService].subtitle}
                          </p>
                        </div>

                        {/* Service Description - Shortened */}
                        <p className="text-white/90 text-sm leading-relaxed mb-6 max-w-xl line-clamp-3">
                          {services[hoveredService].overview}
                        </p>

                        {/* Book Appointment Button */}
                        <button
                          onClick={() => {
                            handleServiceClick(hoveredService);
                            onBookingClick();
                          }}
                          className="bg-amber-500 text-black px-6 py-3 font-bold uppercase text-sm tracking-wider hover:bg-amber-600 hover:text-black transition-all duration-300 rounded-lg shadow-xl hover:shadow-2xl hover:scale-105 inline-flex items-center justify-center gap-2 w-fit"
                        >
                          <span>BOOK APPOINTMENT</span>
                          <span>→</span>
                        </button>
                      </div>
                    </div>

                  </div>
                </div>
              )}
            </div>

            {navLinksAfterServices.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-amber-500'
                    : 'text-white hover:text-amber-500'
                }`}
              >
                {link.label}
              </Link>
            ))}
              </>
            )}

            <a
              href={`tel:${siteInfo.phone.replace(/\s/g, '')}`}
              className="text-white hover:text-amber-500 transition-colors flex items-center gap-2"
            >
              <Phone size={18} />
              <span className="font-bold">{siteInfo.phone}</span>
            </a>
            <button
              onClick={onBookingClick}
              className="bg-amber-500 text-black px-6 py-2 font-bold uppercase tracking-wider hover:bg-amber-600 hover:text-black transition-colors"
            >
              Book Now
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white p-2"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-black border-t border-gray-800">
          <div className="px-4 pt-2 pb-4 space-y-2">
            {/* Hide navigation links on Thank You page */}
            {!isThankYouPage && (
              <>
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block w-full text-left py-2 font-medium transition-colors ${
                      isActive(link.path)
                        ? 'text-amber-500'
                        : 'text-white hover:text-amber-500'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                
                {/* Mobile Services Dropdown */}
                <div>
              <button
                onClick={() => setIsServicesDropdownOpen(!isServicesDropdownOpen)}
                className="block w-full text-left py-2 font-medium text-white hover:text-amber-500 flex items-center justify-between"
              >
                Services
                <ChevronDown size={16} className={`transition-transform ${isServicesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {isServicesDropdownOpen && (
                <div className="pl-4 space-y-2 mt-2">
                  {Object.entries(services).map(([key, service]) => {
                    const IconComponent = serviceIcons[key];
                    return (
                      <button
                        key={key}
                        onClick={() => handleServiceClick(key)}
                        className="block w-full text-left py-2 text-gray-400 hover:text-amber-500 text-sm flex items-center gap-2"
                      >
                        <IconComponent size={18} />
                        <span>{service.title}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {navLinksAfterServices.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block w-full text-left py-2 font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-amber-500'
                    : 'text-white hover:text-amber-500'
                }`}
              >
                {link.label}
              </Link>
            ))}
              </>
            )}

            <a
              href={`tel:${siteInfo.phone.replace(/\s/g, '')}`}
              className="block w-full text-left text-white hover:text-amber-500 py-2 font-medium"
            >
              📞 {siteInfo.phone}
            </a>
            <button
              onClick={() => {
                onBookingClick();
                setIsMenuOpen(false);
              }}
              className="block w-full bg-amber-500 text-black px-6 py-3 mt-4 font-bold uppercase tracking-wider hover:bg-amber-600 hover:text-black transition-colors text-center"
            >
              Book an Appointment
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
